import { Interval } from 'luxon';
import React, { useState } from 'react';
import styled from 'styled-components';

import { InfiniteTable } from 'modules/experience-core';

import ErrorPage from '../components/ErrorPage';
import LoadingPage from '../components/LoadingPage';
import RejectedPurchasesTable from '../components/tables/rejections/RejectedPurchasesTable';
import { RejectedPurchasesTableColumnIDs } from '../components/tables/rejections/types';
import useRejectedPurchasesInsights from '../hooks/useRejectedPurchasesInsights';
import { InsightEntity } from '../types';
import { isAllError, isInitialLoading } from '../utils';

type RejectedPurchasesPageProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
};

const Root = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  flex: 1 1 auto;
`;

const RejectedPurchasesPage = ({ insightEntity, dateInterval }: RejectedPurchasesPageProps) => {
  const entityId = insightEntity.id;
  const [sort, setSort] = useState<InfiniteTable.SortState<RejectedPurchasesTableColumnIDs> | undefined>(undefined);

  const rejectedPurchasesInsights = useRejectedPurchasesInsights({
    entityType: insightEntity.type,
    entityId,
    interval: dateInterval,
    sortBy: sort?.sortBy,
    sortOrder: sort?.sortOrder,
  });
  const {
    data: rejectedPurchasesData,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = rejectedPurchasesInsights;

  const props = {
    insightEntity,
    isFetchingNextPage,
    isFetchingPreviousPage,
    items:
      rejectedPurchasesData?.pages.flatMap(page => {
        return page.data.purchases.map(item => ({ ...item, id: item.purchaseId, currency: page?.currency }));
      }) ?? [],
    sort,
    handleSort: (sort: InfiniteTable.SortState<RejectedPurchasesTableColumnIDs>) => setSort(sort),
    onFetchNextPage: hasNextPage ? fetchNextPage : undefined,
    onFetchPreviousPage: hasPreviousPage ? fetchPreviousPage : undefined,
  };

  if (isInitialLoading([rejectedPurchasesInsights])) {
    return <LoadingPage />;
  }
  if (isAllError([rejectedPurchasesInsights])) {
    return (
      <ErrorPage
        onRetry={() => {
          rejectedPurchasesInsights.refetch();
        }}
      />
    );
  }

  return (
    <Root>
      <RejectedPurchasesTable {...props} />
    </Root>
  );
};

export default RejectedPurchasesPage;
